import {
  BackSide,
  Color,
  DoubleSide,
  FrontSide,
  NormalBlending,
  ShaderMaterial,
  UniformsLib,
  UniformsUtils,
  Vector2,
  Vector3,
  Vector4,
  type Texture,
} from 'three'
import type { ManifestMaterial, ManifestRGBA, ManifestToon } from './assetLibrary'
import { revealMaterialAt } from './palette'

/**
 * A port of the Stylized Toon shader family, which is what draws every surface
 * in this game.
 *
 * The bundle ships four compiled variants — `Custom/StylizedToon`,
 * `Custom/Char`, `Custom/Ground` and `Custom/ShadowBlob` — and they are not
 * interchangeable. `Ground` is the only one with the triplanar mask detail that
 * puts grain on the background plate, `Char` is the only one with a fresnel rim,
 * and `ShadowBlob` has no lighting term at all. Treating all of them as the base
 * variant is why the ground read as a flat wash of colour.
 *
 * AssetRipper could not recover any of the HLSL — every one exports as a
 * `DummyShaderTextExporter` stub — but two things did survive, and between them
 * they pin the model down: each shader's `Properties` block, which names every
 * knob, its type and its default, and each material's own values.
 *
 * Lighting is not Lambert. `N·L` is pushed through a smoothstep centred on
 * `_RampThreshold` and `_RampSmoothing` wide, and the result picks between
 * `_HighlightColor` and a shadow tint; `_ShadowColorLift` blends that tint
 * toward the ambient probe, scaled by `_AmbientBoost`. That is what gives the
 * shipped game flat panels with one crisp terminator instead of a gradient.
 *
 * ## Colour space
 *
 * Every colour read out of a `.mat` is **sRGB**, not linear, and the difference
 * is not subtle. `Bus_Red`'s palette entry is (0.858, 0.203, 0.028); taken as
 * linear and encoded for display it becomes (0.938, 0.489, 0.187) — a bus that
 * reads plainly orange, which is what the port drew until this was measured
 * against a screenshot of the original. `Char_Yellow` settles it numerically:
 * its R/G ratio is 1.25 as authored and 1.10 if the value is treated as linear,
 * and the shipped game measures 1.26.
 *
 * ## What is reconstructed rather than recovered
 *
 * The ramp, the palette blend and the matcap projection follow from the
 * property names and the observed result. Three details are a considered
 * reading rather than a certainty, and are marked at their use: how a detail
 * map's brightness/strength/midpoint combine, how `_MatcapContrast` curves, and
 * that `_RimMin` > `_RimMax` means the band is taken between them rather than
 * inverted. Each was chosen because it reproduces the reference frame; none of
 * the knobs left out (dissolve, sheen, transmission) is switched on by any
 * material in this scene.
 */

/**
 * The scene's light, shared by every material.
 *
 * `Gameplay.unity` has exactly one directional light and a flat ambient colour
 * (`m_AmbientMode: 3`), so there is nothing to iterate over and no reason to go
 * through three's light plumbing. Both are held here as single objects whose
 * identity every material references, so setting them once updates the scene.
 */
export const ToonLighting = {
  /** Which way the sun shines, normalised, in three's space. */
  direction: { value: new Vector3(-0.1485, -0.9532, 0.2634).normalize() },
  /** `RenderSettings.m_AmbientSkyColor`, which is authored in sRGB. */
  ambient: { value: new Color().setRGB(0.6862745, 0.70980394, 0.70980394, 'srgb') },
}

/**
 * The stretch of the loop that has tarmac on it. Anything a bus draws outside
 * it is discarded.
 *
 * The loop is longer than the road swept onto it. Only
 * `OriginalStartPercent`..`OriginalEndPercent` gets tarmac, and the 19.3-unit
 * remainder — the same on every level — is **two straight 9.67-unit stubs**,
 * one running out of each end, with a `ConveyorCover` standing over each mouth.
 * A bus driving out there has nothing under it, and the shipped game stops
 * drawing it: `BusSplineFollower.UpdateInvisibility` moves the bus and every
 * rider on it to the `Invisible` layer (7 in `TagManager`) and back out again.
 *
 * A whole-bus switch cannot be the whole story, and the geometry says why. The
 * gap is 19.3 units; a bus on the road measures 8 to 19. At the moment any
 * switch could fire, most of the body is still out on open tarmac, so it would
 * pop. What the covers carry instead is a stencil volume — their `Cube` child
 * wears `Invisible2`, a `RuffGames/invisible` material whose only property is
 * `_StencilID: 3` — and the renderer feature that consumes it is a
 * MonoBehaviour IL2CPP stripped to an empty shell (`URP_Renderer.asset` and
 * `RenderObjects.asset` both export as nothing but a name). So the exact pass
 * is not recoverable. That the bus is *masked* rather than blinked out is.
 *
 * ❓ **The cut is a port-side device, not a reading.** The original hides; this
 * cuts. What is recovered is only that a mask exists and that its edge is
 * hidden under the cover. Declared in `GAMEPLAY-CHECKLIST.md` §10.
 *
 * ## Why it is measured in arc distance and not in world space
 *
 * 🔴 This used to be **two boxes in world space** — past a mouth's plane, no
 * wider than `TUNNEL_HALF_WIDTH`, no deeper than the stub — chosen per bus by a
 * `ClipMask` that `UpdateInvisibility` supplied. Both halves of that were
 * wrong, and each cost a bug the player could see:
 *
 *  - **The mask.** `UpdateInvisibility` answers *"hide the WHOLE bus?"*. In the
 *    straddling case, once the nose is back on tarmac the answer is no — so the
 *    mask went to 0 while the port was still drawing a **second copy** of the
 *    body out on the far stub (`BusFleet.seamBranch`). Nothing armed, nothing
 *    cut: measured over the forty levels, **38 of them** drew up to **7.66
 *    units** of bus rear on bare ground past the cover, flickering as the mask
 *    flipped 3 → 0 → 3 between frames.
 *  - **The box.** A volume in world space catches whatever happens to stand in
 *    it. Level 19's hairpin runs **5.54 units** of open tarmac through the end
 *    mouth's box, **87.63 arc-units** back from the end it belongs to, and
 *    buses crossing it were sliced in the middle of the road.
 *
 * Both go away once the cut asks the question the rule actually asks — *"is
 * this fragment past the end of the tarmac?"* — which is one dimension, along
 * the spline, not three in the world. The hairpin cannot be caught, because arc
 * distance 59 is not in `[0, 9.67] ∪ [143.72, 153.39]`. The rear cannot escape,
 * because every vertex carries its own arc distance and no per-bus verdict
 * stands between it and the test.
 *
 * ## Why it needs no per-mask material
 *
 * The window is a **level constant, the same for every bus**, so one uniform
 * shared by identity serves the whole level. The old planes were per bus, which
 * is why they needed one material per mask: three uploads a `ShaderMaterial`'s
 * uniforms only when the material differs from the last draw's
 * (`refreshMaterial` in `WebGLRenderer.setProgram`), and the opaque pass is
 * sorted by material id, so every bus sharing a colour drew off a single upload
 * and whichever drew first decided the planes for all of them.
 *
 * What is left is one bit: does this material read `aRoadDistance` at all.
 * Geometry that is not laid out along the road — parked buses, the lot, the
 * queue, the scenery — carries no such attribute and must not be cut, so it
 * keeps the plain material.
 */
export const RoadWindow = {
  /** x = `OriginalStartPercent` as arc distance, y = `OriginalEndPercent`. */
  value: new Vector2(0, 0),
}

/** The material reads `aRoadDistance` and discards outside `RoadWindow`. */
export const ROAD_CUT = 1

/**
 * Whether a material cuts against the drawn road — `ROAD_CUT`, or 0 for
 * everything that is not laid out along the spline.
 */
export type ClipMask = 0 | 1

/** Read a manifest colour the way Unity authored it: sRGB, not linear. */
export function toonColor(c: ManifestRGBA, into = new Color()): Color {
  return into.setRGB(c.r, c.g, c.b, 'srgb')
}

/**
 * 🔍 `Bus.PlayRevealMaterialTween` at `elapsed` seconds, written onto one
 * material instance — the port's `mat.DOFloat(…, DissolveRange)` and
 * `mat.DOColor(…, ShadowColor)` for a single frame.
 *
 * `material` has to be a per-bus instance (`AssetLibrary.revealing`), never the
 * shared `Bus_<Colour>` object: three uploads a ShaderMaterial's uniforms only
 * when the material changes between draws, and every bus of a colour draws with
 * the same one, so a value written per bus onto the shared object reaches the
 * first bus of the group and no other. The original has the same shape for a
 * different reason — `Renderer.materials` instantiates per renderer.
 *
 * `spec` is the **plain** material's manifest entry (its `toon.shadow` is the
 * tween's target); `material` was built from `hiddenMaterialSpec(spec)`, so it
 * carries the dissolve branch and starts at range 0, shadow black.
 */
export function writeRevealTween(
  material: ToonMaterial,
  spec: ManifestMaterial,
  elapsed: number,
): void {
  const at = revealMaterialAt(spec, elapsed)
  const range = material.uniforms.uDissolveRange as { value: number } | undefined
  if (range) range.value = at.dissolveRange
  toonColor(at.shadow, material.uniforms.uShadow.value as Color)
}

const VERTEX = /* glsl */ `
varying vec3 vWorldNormal;
varying vec3 vViewNormal;
varying vec3 vWorldPosition;
varying vec2 vTexCoord;
#ifdef USE_ROAD_CUT
  // How far along the road this vertex sits, written every frame by
  // BusFleet.poseBranch from the same along that placed it.
  attribute float aRoadDistance;
  varying float vRoadDistance;
#endif
#ifdef USE_COLOR
  varying vec3 vMask;
#endif
#ifdef RECEIVE_SHADOWS
  // three's own declarations: directionalShadowMatrix, the per-light bias
  // struct and the vDirectionalShadowCoord varying. Only the materials whose
  // .mat receives (see ManifestMaterial.receiveShadows) pay for them.
  #include <shadowmap_pars_vertex>
#endif

void main() {
  // Instancing has to be applied by hand.
  //
  // A built-in material gets it from the begin_vertex / project_vertex chunks;
  // a ShaderMaterial writing its own main() does not, and skipping it does not
  // fail loudly — every instance simply collapses onto the origin, which is
  // where the entire passenger population went the first time.
  mat4 instance = mat4(1.0);
  #ifdef USE_INSTANCING
    instance = instanceMatrix;
  #endif

  vec4 world = modelMatrix * instance * vec4(position, 1.0);
  vWorldPosition = world.xyz;

  // World space, not view space: the sun is a world direction, and a camera
  // that pans would otherwise drag the terminator across the level.
  vWorldNormal = normalize(mat3(modelMatrix * instance) * normal);
  // The matcap needs the same normal in view space; deriving it from the world
  // one keeps instancing correct without a second normal matrix.
  vViewNormal = normalize(mat3(viewMatrix) * vWorldNormal);
  vTexCoord = uv;
  #ifdef USE_ROAD_CUT
    vRoadDistance = aRoadDistance;
  #endif
  #ifdef USE_COLOR
    vMask = color;
  #endif

  #if defined(RECEIVE_SHADOWS) && defined(USE_SHADOWMAP) && NUM_DIR_LIGHT_SHADOWS > 0
    // The same coordinate three's shadowmap_vertex chunk writes, spelled out
    // because that chunk expects the built-in materials' worldPosition and
    // transformedNormal. One directional light: Gameplay.unity has exactly
    // one, so index 0 is the sun.
    vDirectionalShadowCoord[0] = directionalShadowMatrix[0]
      * (world + vec4(vWorldNormal * directionalLightShadows[0].shadowNormalBias, 0.0));
  #endif

  gl_Position = projectionMatrix * viewMatrix * world;
}
`

const FRAGMENT = /* glsl */ `
uniform vec3 uBaseColor;
uniform float uOpacity;
uniform vec4 uMapST;
uniform vec3 uHighlight;
uniform vec3 uShadow;
uniform float uRampThreshold;
uniform float uRampSmoothing;
uniform float uAmbientBoost;
uniform float uShadowLift;
uniform vec3 uSunDirection;
uniform vec3 uAmbient;
#ifdef USE_ROAD_CUT
  uniform vec2 uRoadWindow;
  varying float vRoadDistance;
#endif

#ifdef USE_MAP
  uniform sampler2D uMap;
#endif

#ifdef USE_VERTEX_PALETTE
  uniform vec3 uPaletteR;
  uniform vec3 uPaletteG;
  uniform vec3 uPaletteB;
#endif

#if defined(CHANNEL_TEX_R) || defined(CHANNEL_TEX_G) || defined(CHANNEL_TEX_B)
  #define ANY_CHANNEL_TEX
#endif
#if defined(ANY_CHANNEL_TEX) || defined(USE_MASK_DETAIL)
  #define ANY_DETAIL
#endif

#ifdef CHANNEL_TEX_R
  uniform sampler2D uChannelMapR;
  uniform vec4 uChannelSTR;
  uniform vec3 uChannelParamsR;
#endif
#ifdef CHANNEL_TEX_G
  uniform sampler2D uChannelMapG;
  uniform vec4 uChannelSTG;
  uniform vec3 uChannelParamsG;
#endif
#ifdef CHANNEL_TEX_B
  uniform sampler2D uChannelMapB;
  uniform vec4 uChannelSTB;
  uniform vec3 uChannelParamsB;
#endif

#ifdef USE_MASK_DETAIL
  uniform sampler2D uMaskMap;
  uniform vec4 uMaskST;
  // brightness, centre scale, strength, midpoint
  uniform vec4 uMaskParams;
#endif

#ifdef USE_VIGNETTE
  uniform vec3 uVignetteColor;
  // threshold, smoothing, alpha, rounding
  uniform vec4 uVignetteParams;
#endif

#ifdef USE_DISSOLVE
  uniform sampler2D uDissolveMap;
  uniform vec4 uDissolveST;
  uniform vec3 uDissolveHidden;
  uniform float uDissolveAlpha;
  // _DissolveRange: 0 fully wrapped, 1 the ordinary surface.
  uniform float uDissolveRange;
#endif

#ifdef USE_MATCAP
  uniform sampler2D uMatcapMap;
  uniform vec3 uMatcapColor;
  // strength, contrast, unused
  uniform vec3 uMatcapParams;
  uniform vec3 uMatcapChannels;
#endif

#ifdef USE_RIM
  // base shade, min, max, strength
  uniform vec4 uRimParams;
#endif

varying vec3 vWorldNormal;
varying vec3 vViewNormal;
varying vec3 vWorldPosition;
varying vec2 vTexCoord;
#ifdef USE_COLOR
  varying vec3 vMask;
#endif
#ifdef RECEIVE_SHADOWS
  // getShadow() and the directionalShadowMap sampler; packing supplies the
  // depth decode it reads with.
  #include <packing>
  #include <shadowmap_pars_fragment>
#endif

#ifdef ANY_DETAIL
/**
 * A detail map's multiplier.
 *
 * Reconstructed, not recovered. The shape is fixed at both ends: a slot whose
 * texture defaults to "gray" must be a no-op at the default midpoint of 0.5,
 * and _BaseBrightness must survive as a plain scale, so the only form that
 * fits is brightness * (1 + (sample - midpoint) * strength). Every material
 * here lands in a believable range under it.
 */
float detailFactor(float sample_, vec3 params) {
  return params.x * (1.0 + (sample_ - params.z) * params.y);
}

/**
 * Sample a map projected on the three world axes and blend by the normal.
 *
 * This is what _UseVertexColor*Triplanar and _MaskDetailType = Triplanar
 * ask for, and it is the only way the raised plate's checkerboard can run
 * unbroken across a mesh whose UVs were never laid out for it.
 */
float triplanar(sampler2D map, vec3 worldPos, vec3 normal, vec2 scale, vec2 offset) {
  vec3 blend = normalize(max(abs(normal), 0.00001));
  blend /= blend.x + blend.y + blend.z;
  float x = texture2D(map, worldPos.zy * scale + offset).r;
  float y = texture2D(map, worldPos.xz * scale + offset).r;
  float z = texture2D(map, worldPos.xy * scale + offset).r;
  return x * blend.x + y * blend.y + z * blend.z;
}
#endif

#ifdef USE_DISSOLVE
/**
 * The same projection kept whole, for the one map that is a picture rather
 * than a detail multiplier: the dissolve wrapper needs the colour and, above
 * all, the alpha, which is where the question-mark shape lives.
 */
vec4 triplanarRGBA(sampler2D map, vec3 worldPos, vec3 normal, vec2 scale, vec2 offset) {
  vec3 blend = normalize(max(abs(normal), 0.00001));
  blend /= blend.x + blend.y + blend.z;
  vec4 x = texture2D(map, worldPos.zy * scale + offset);
  vec4 y = texture2D(map, worldPos.xz * scale + offset);
  vec4 z = texture2D(map, worldPos.xy * scale + offset);
  return x * blend.x + y * blend.y + z * blend.z;
}
#endif

void main() {
  // The road ends before the loop does. A fragment past either end of the
  // drawn tarmac is out in a tunnel stub with nothing under it -- see
  // RoadWindow. No per-bus verdict gates this: the vertex own arc distance is
  // the whole test.
  #ifdef USE_ROAD_CUT
    if (vRoadDistance < uRoadWindow.x || vRoadDistance > uRoadWindow.y) discard;
  #endif

  vec4 texel = vec4(1.0);
  #ifdef USE_MAP
    texel = texture2D(uMap, vTexCoord * uMapST.xy + uMapST.zw);
  #endif

  vec3 albedo = uBaseColor * texel.rgb;

  #ifdef USE_VERTEX_PALETTE
    // The mask is not a colour. A vertex carries (1,0,0), (0,1,0) or (1,1,1) to
    // pick a shade and (0,0,0) to keep the base texture untouched, and the
    // shader blends the palette in channel order, so a later channel wins where
    // masks overlap — which is why (1,1,1) reads as the B shade.
    vec3 tint = vec3(1.0);
    tint += (uPaletteR - tint) * vMask.r;
    tint += (uPaletteG - tint) * vMask.g;
    tint += (uPaletteB - tint) * vMask.b;

    // Per-channel detail maps ride on the same mask, applied only as far as
    // that channel is set.
    #ifdef CHANNEL_TEX_R
      {
        #ifdef CHANNEL_TRIPLANAR_R
          float d = triplanar(uChannelMapR, vWorldPosition, vWorldNormal, uChannelSTR.xy, uChannelSTR.zw);
        #else
          float d = texture2D(uChannelMapR, vTexCoord * uChannelSTR.xy + uChannelSTR.zw).r;
        #endif
        tint *= mix(1.0, detailFactor(d, uChannelParamsR), vMask.r);
      }
    #endif
    #ifdef CHANNEL_TEX_G
      {
        #ifdef CHANNEL_TRIPLANAR_G
          float d = triplanar(uChannelMapG, vWorldPosition, vWorldNormal, uChannelSTG.xy, uChannelSTG.zw);
        #else
          float d = texture2D(uChannelMapG, vTexCoord * uChannelSTG.xy + uChannelSTG.zw).r;
        #endif
        tint *= mix(1.0, detailFactor(d, uChannelParamsG), vMask.g);
      }
    #endif
    #ifdef CHANNEL_TEX_B
      {
        #ifdef CHANNEL_TRIPLANAR_B
          float d = triplanar(uChannelMapB, vWorldPosition, vWorldNormal, uChannelSTB.xy, uChannelSTB.zw);
        #else
          float d = texture2D(uChannelMapB, vTexCoord * uChannelSTB.xy + uChannelSTB.zw).r;
        #endif
        tint *= mix(1.0, detailFactor(d, uChannelParamsB), vMask.b);
      }
    #endif

    albedo *= tint;
  #endif

  #ifdef USE_MASK_DETAIL
    {
      #ifdef MASK_TRIPLANAR
        // _CenterTextureScale is the triplanar's own world scale; the slot's
        // tiling still applies on top of it.
        vec2 scale = uMaskST.xy / max(uMaskParams.y, 0.00001);
        float d = triplanar(uMaskMap, vWorldPosition, vWorldNormal, scale, uMaskST.zw);
      #else
        float d = texture2D(uMaskMap, vTexCoord * uMaskST.xy + uMaskST.zw).r;
      #endif
      albedo *= detailFactor(d, vec3(uMaskParams.x, uMaskParams.z, uMaskParams.w));
    }
  #endif

  #ifdef USE_DISSOLVE
    // Bus.ApplyHiddenMaterialState writes _DissolveRange = 0, and
    // PlayRevealMaterialTween tweens it back to 1 over 0.893 s. So 0 is fully
    // wrapped and 1 is the ordinary surface, and this lerps between them.
    //
    // What the wrapper is, and why it is projected rather than unwrapped, is
    // written up on palette.dissolveMix.
    if (uDissolveRange < 0.999) {
      vec4 stamp = triplanarRGBA(
        uDissolveMap, vWorldPosition, vWorldNormal, uDissolveST.xy, uDissolveST.zw
      );
      vec3 wrapped = mix(uDissolveHidden, stamp.rgb, stamp.a * uDissolveAlpha);
      albedo = mix(wrapped, albedo, uDissolveRange);
    }
  #endif

  #ifdef UNLIT
    // Custom/ShadowBlob has no lighting term at all — it is base colour times
    // base map and nothing else, which is what makes the buses' ground blob a
    // flat stencil rather than a lit surface.
    vec3 lit = albedo;
  #else
    // Half lambert: N·L remapped from -1..1 to 0..1 before the ramp.
    //
    // The shaders' own Properties blocks settle this. _RampThreshold is declared
    // Range(0, 1), and a threshold that cannot go negative is meaningless
    // against a raw N·L — 0.5, its default and what every bus material uses,
    // would sit at the *lit* side rather than the terminator. Taking it raw also
    // turns every side-facing panel fully black wherever _ShadowColor is black
    // and _ShadowColorLift is 0, which is most of the bus palette.
    float ndl = dot(normalize(vWorldNormal), -uSunDirection) * 0.5 + 0.5;
    // A zero-width ramp would make smoothstep's edges equal, which is undefined.
    float halfWidth = max(uRampSmoothing * 0.5, 1e-4);
    float ramp = smoothstep(uRampThreshold - halfWidth, uRampThreshold + halfWidth, ndl);

    // The sun's shadow map, on the surfaces whose .mat receives it.
    //
    // three's getShadow already returns mix(1, occlusion, intensity), which is
    // URP's LerpWhiteTo(attenuation, _MainLightShadowParams.x) — the light's
    // m_Strength, 🔍 0.382 in Gameplay.unity, set on the light in sunShadow.ts.
    // So a fully shadowed texel comes back as 0.618, never 0.
    //
    // ❓ PLACEHOLDER — **where** the factor goes. The HLSL of all four
    // Custom/* shaders is an exporter stub, so this is the one term the
    // Properties block does not pin down. It is folded into the ramp, which
    // makes a cast shadow the same colour as the surface's own unlit side —
    // the toon reading, and what _ShadowColor is for. The other candidate,
    // scaling N·L before the smoothstep, would push the ground's 0.804 ramp to
    // 0.27 instead of 0.497 and make every cast shadow far darker than the
    // terminator. Declared in GAMEPLAY-CHECKLIST.md §10.
    #if defined(RECEIVE_SHADOWS) && defined(USE_SHADOWMAP) && NUM_DIR_LIGHT_SHADOWS > 0
      ramp *= getShadow(
        directionalShadowMap[0],
        directionalLightShadows[0].shadowMapSize,
        directionalLightShadows[0].shadowIntensity,
        directionalLightShadows[0].shadowBias,
        directionalLightShadows[0].shadowRadius,
        vDirectionalShadowCoord[0]
      );
    #endif

    vec3 shadowTint = mix(uShadow, uAmbient * uAmbientBoost, uShadowLift);
    vec3 lit = albedo * mix(shadowTint, uHighlight, ramp);
  #endif

  #ifdef USE_MATCAP
    {
      // The classic projection: the view-space normal's xy, remapped to UV.
      vec2 muv = normalize(vViewNormal).xy * 0.5 + 0.5;
      vec3 mc = texture2D(uMatcapMap, muv).rgb * uMatcapColor;
      // _MatcapContrast reconstructed as a push around mid grey, which is the
      // only reading under which its 0 default is a no-op.
      mc = clamp((mc - 0.5) * (1.0 + uMatcapParams.y * 2.0) + 0.5, 0.0, 1.0);
      // _MatcapChannelMask is a bitfield over the vertex-colour mask, so a
      // material can put a sheen on the body panels and leave the trim alone.
      float coverage = 1.0;
      #ifdef USE_COLOR
        coverage = clamp(dot(uMatcapChannels, vMask), 0.0, 1.0);
        coverage = mix(1.0, coverage, step(0.5, uMatcapChannels.x + uMatcapChannels.y + uMatcapChannels.z));
      #endif
      // Additive, not a multiply: every matcap in this bundle is a dark sphere
      // with one bright spot, and multiplying by it would halve the scene.
      lit += mc * uMatcapParams.x * coverage;
    }
  #endif

  #ifdef USE_RIM
    {
      float fresnel = 1.0 - abs(normalize(vViewNormal).z);
      // _RimMin (1.0) is above _RimMax (0.546) on every material that uses
      // this, so the band is taken *between* them: a thin darkened edge at the
      // silhouette, which is the outline visible around each car and passenger
      // in the shipped game.
      float lo = min(uRimParams.y, uRimParams.z);
      float hi = max(uRimParams.y, uRimParams.z);
      float edge = clamp(smoothstep(lo, hi, fresnel) * uRimParams.w, 0.0, 1.0);
      lit = mix(lit, lit * (1.0 + uRimParams.x), edge);
    }
  #endif

  #ifdef USE_VIGNETTE
    {
      // A superellipse in the surface's own UV: _VignetteRounding moves the
      // falloff between a circle and a rounded rectangle.
      vec2 p = abs(vTexCoord * 2.0 - 1.0);
      float k = mix(2.0, 8.0, clamp(uVignetteParams.w, 0.0, 1.0));
      float d = pow(pow(p.x, k) + pow(p.y, k), 1.0 / k);
      float m = smoothstep(uVignetteParams.x, uVignetteParams.x + uVignetteParams.y, d);
      lit = mix(lit, uVignetteColor, m * uVignetteParams.z);
    }
  #endif

  gl_FragColor = vec4(lit, uOpacity * texel.a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`

/** Resolves a texture name from the manifest to a loaded texture. */
export type TextureResolver = (name: string) => Texture | null

const ZERO = { r: 0, g: 0, b: 0, a: 1 }

export class ToonMaterial extends ShaderMaterial {
  /** The manifest entry this was built from, for callers that need to re-read it. */
  readonly spec: ManifestMaterial | null

  /** Whether this material cuts against the drawn road. */
  readonly clip: ClipMask

  /**
   * 📐 Whether the material has a shadow-caster pass — false for the six
   * `.mat` files that disable `SHADOWCASTER`. Read by `applyShadowCasting`;
   * three itself has no per-material cast switch.
   */
  readonly castShadows: boolean

  /**
   * @param clip `ROAD_CUT` to read `aRoadDistance` and discard outside the
   *   drawn road, 0 to draw whole. One material per value rather than a uniform
   *   written per draw — see `RoadWindow`.
   */
  constructor(
    spec: ManifestMaterial | null = null,
    resolve: TextureResolver = () => null,
    clip: ClipMask = 0,
  ) {
    const toon: Partial<ManifestToon> = spec?.toon ?? {}
    const palette = spec?.palette ?? null
    const defines: Record<string, string> = {}
    const uniforms: Record<string, { value: unknown }> = {
      uBaseColor: { value: toonColor(spec?.color ?? { r: 1, g: 1, b: 1, a: 1 }) },
      uOpacity: { value: spec?.color.a ?? 1 },
      uMapST: {
        value: new Vector4(
          toon.baseScale?.[0] ?? 1,
          toon.baseScale?.[1] ?? 1,
          toon.baseOffset?.[0] ?? 0,
          toon.baseOffset?.[1] ?? 0,
        ),
      },
      uHighlight: { value: toonColor(toon.highlight ?? { r: 1, g: 1, b: 1, a: 1 }) },
      uShadow: { value: toonColor(toon.shadow ?? ZERO) },
      uRampThreshold: { value: toon.rampThreshold ?? 0.5 },
      // A material with ramp shading switched off wants a plain terminator,
      // which a smoothstep wider than the whole 0..1 range is.
      uRampSmoothing: { value: toon.useRamp === false ? 2 : (toon.rampSmoothing ?? 0.5) },
      uAmbientBoost: { value: toon.ambientBoost ?? 1 },
      uShadowLift: { value: toon.shadowColorLift ?? 0.5 },
      // Shared by identity, so `ToonLighting` drives every material at once.
      uSunDirection: ToonLighting.direction,
      uAmbient: ToonLighting.ambient,
    }

    // Shared by identity like the light, so the single write in
    // `SessionView.buildRoadWindow` reaches every cutting material at once.
    if (clip) {
      defines.USE_ROAD_CUT = ''
      uniforms.uRoadWindow = RoadWindow
    }

    const base = spec?.texture ? resolve(spec.texture) : null
    if (base) {
      defines.USE_MAP = ''
      uniforms.uMap = { value: base }
    }

    if (toon.shader === 'ShadowBlob') defines.UNLIT = ''

    // 📐 The sun's shadow map, only where the .mat asks for it. `lights: true`
    // is what makes three hand a ShaderMaterial the shadow uniforms, and it
    // insists on the whole UniformsLib.lights block being present — cloned,
    // not shared, because the renderer assigns into these per material.
    const receiveShadows = spec?.receiveShadows === true
    if (receiveShadows) {
      defines.RECEIVE_SHADOWS = ''
      Object.assign(uniforms, UniformsUtils.clone(UniformsLib.lights))
    }

    if (palette) {
      defines.USE_VERTEX_PALETTE = ''
      uniforms.uPaletteR = { value: toonColor(palette.r ?? { r: 1, g: 1, b: 1, a: 1 }) }
      uniforms.uPaletteG = { value: toonColor(palette.g ?? { r: 1, g: 1, b: 1, a: 1 }) }
      uniforms.uPaletteB = { value: toonColor(palette.b ?? { r: 1, g: 1, b: 1, a: 1 }) }

      for (const [letter, slot] of [
        ['R', palette.rTexture],
        ['G', palette.gTexture],
        ['B', palette.bTexture],
      ] as const) {
        if (!slot) continue
        const map = resolve(slot.texture)
        if (!map) continue
        defines[`CHANNEL_TEX_${letter}`] = ''
        if (slot.triplanar) defines[`CHANNEL_TRIPLANAR_${letter}`] = ''
        uniforms[`uChannelMap${letter}`] = { value: map }
        uniforms[`uChannelST${letter}`] = {
          value: new Vector4(slot.scale[0], slot.scale[1], slot.offset[0], slot.offset[1]),
        }
        uniforms[`uChannelParams${letter}`] = {
          value: new Vector3(slot.brightness, slot.strength, slot.midpoint),
        }
      }
    }

    if (toon.maskDetail) {
      const map = resolve(toon.maskDetail.texture)
      if (map) {
        defines.USE_MASK_DETAIL = ''
        if (toon.maskDetail.triplanar) defines.MASK_TRIPLANAR = ''
        uniforms.uMaskMap = { value: map }
        uniforms.uMaskST = {
          value: new Vector4(
            toon.maskDetail.scale[0],
            toon.maskDetail.scale[1],
            toon.maskDetail.offset[0],
            toon.maskDetail.offset[1],
          ),
        }
        uniforms.uMaskParams = {
          value: new Vector4(
            toon.maskDetail.brightness,
            toon.maskDetail.centerScale,
            toon.maskDetail.strength,
            toon.maskDetail.midpoint,
          ),
        }
      }
    }

    // Only switched on for a material that is actually meant to be wrapped —
    // `spec.dissolveRange` is set by `palette.hiddenMaterialSpec` and by nothing
    // else, so an ordinary bus pays neither the sampler nor the branch.
    if (toon.dissolve && spec?.dissolveRange !== undefined) {
      const map = resolve(toon.dissolve.texture)
      if (map) {
        defines.USE_DISSOLVE = ''
        uniforms.uDissolveMap = { value: map }
        uniforms.uDissolveST = {
          value: new Vector4(
            toon.dissolve.scale[0],
            toon.dissolve.scale[1],
            toon.dissolve.offset[0],
            toon.dissolve.offset[1],
          ),
        }
        uniforms.uDissolveHidden = { value: toonColor(toon.dissolve.hidden) }
        uniforms.uDissolveAlpha = { value: toon.dissolve.textureAlpha }
        uniforms.uDissolveRange = { value: spec.dissolveRange }
      }
    }

    if (toon.vignette) {
      defines.USE_VIGNETTE = ''
      uniforms.uVignetteColor = { value: toonColor(toon.vignette.color) }
      uniforms.uVignetteParams = {
        value: new Vector4(
          toon.vignette.threshold,
          toon.vignette.smoothing,
          toon.vignette.alpha,
          toon.vignette.rounding,
        ),
      }
    }

    if (toon.matcap) {
      const map = resolve(toon.matcap.texture)
      if (map) {
        const mask = toon.matcap.channelMask
        defines.USE_MATCAP = ''
        uniforms.uMatcapMap = { value: map }
        uniforms.uMatcapColor = { value: toonColor(toon.matcap.color) }
        uniforms.uMatcapParams = {
          value: new Vector3(toon.matcap.strength, toon.matcap.contrast, 0),
        }
        uniforms.uMatcapChannels = {
          value: new Vector3(mask & 1 ? 1 : 0, mask & 2 ? 1 : 0, mask & 4 ? 1 : 0),
        }
      }
    }

    if (toon.rim) {
      defines.USE_RIM = ''
      uniforms.uRimParams = {
        value: new Vector4(toon.rim.baseShade, toon.rim.min, toon.rim.max, toon.rim.strength),
      }
    }

    super({
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      uniforms: uniforms as never,
      defines,
      // Even a material with no palette needs the attribute bound when its mesh
      // carries one, because the matcap's channel mask reads it.
      vertexColors: palette !== null || toon.matcap !== undefined,
      lights: receiveShadows,
    })

    this.spec = spec
    this.clip = clip
    this.castShadows = spec?.castShadows !== false
    this.name = spec?.name ?? ''

    if (toon.renderFace !== undefined) {
      this.side = toon.renderFace === 0 ? DoubleSide : toon.renderFace === 2 ? BackSide : FrontSide
    }
    if (toon.depthWrite !== undefined) this.depthWrite = toon.depthWrite
    const alpha = spec?.color.a ?? 1
    if (toon.transparent || alpha < 0.999) {
      this.transparent = true
      this.blending = NormalBlending
      this.setOpacity(alpha)
    }

    // `RuffGames/invisible` — `Invisible` (`_StencilID: 2`, the plate across
    // the elevator shaft mouth, `Lift.obj` submesh 1) and `Invisible2`
    // (`_StencilID: 3`, the tunnel cover's blocker `Cube`). Its only property is
    // the stencil id; the render feature that reads the ids is stripped to a
    // name, and the `.shader` text is an exporter stub. 📐 It paints no colour:
    // read from the name, from the property list, and from the reference clip,
    // which shows no white cube over either tunnel mouth. ❓ What the stencil
    // *masks* is not recovered here — the tunnel side is the arc-distance cut
    // (`RoadWindow`), the shaft side is ELV-7. Here it only stops the plate
    // from being drawn as an opaque white `StylizedToon` (ELV-6). Both the
    // WebGL renderer and `renderPreview` skip a draw group whose material has
    // `visible === false`, so the mesh keeps its group layout.
    if (toon.shader === 'invisible') this.visible = false
  }

  /** The base colour, so callers can read and tint it. */
  get color(): Color {
    return this.uniforms.uBaseColor.value as Color
  }

  set map(texture: Texture | null) {
    if (!this.uniforms.uMap) this.uniforms.uMap = { value: null }
    this.uniforms.uMap.value = texture
    const wanted = texture !== null
    if (Boolean(this.defines.USE_MAP !== undefined) !== wanted) {
      if (wanted) this.defines.USE_MAP = ''
      else delete this.defines.USE_MAP
      this.needsUpdate = true
    }
  }

  get map(): Texture | null {
    return (this.uniforms.uMap?.value as Texture | null) ?? null
  }

  /** Mirror `opacity` onto the uniform the shader actually reads. */
  setOpacity(value: number): void {
    this.opacity = value
    this.uniforms.uOpacity.value = value
  }

  /** The base-map tiling and offset, for callers that re-map a swept mesh. */
  get mapTransform(): Vector4 {
    return this.uniforms.uMapST.value as Vector4
  }
}

